import { cn } from "@/lib/utils";

// Import real platform icon SVGs
import spotifyIcon from "@/assets/platform-icons/spotify.svg";
import youtubeMusicIcon from "@/assets/platform-icons/youtube-music.svg";
import tuneinIcon from "@/assets/platform-icons/tunein.svg";
import truefansIcon from "@/assets/platform-icons/truefans.svg";
import rssradioIcon from "@/assets/platform-icons/rssradio.svg";
import podverseIcon from "@/assets/platform-icons/podverse.svg";
import poduramaIcon from "@/assets/platform-icons/podurama.svg";
import podstationIcon from "@/assets/platform-icons/podstation.svg";
import podcastRepublicIcon from "@/assets/platform-icons/podcast-republic.svg";
import podcastGuruIcon from "@/assets/platform-icons/podcast-guru.svg";
import podcastAppIcon from "@/assets/platform-icons/podcast-app.svg";
import podcastAddictIcon from "@/assets/platform-icons/podcast-addict.svg";
import podbeanIcon from "@/assets/platform-icons/podbean.svg";
import pocketcastsIcon from "@/assets/platform-icons/pocketcasts.svg";
import playerFmIcon from "@/assets/platform-icons/player-fm.svg";
import pandoraIcon from "@/assets/platform-icons/pandora.svg";
import overcastIcon from "@/assets/platform-icons/overcast.svg";
import moonFmIcon from "@/assets/platform-icons/moon-fm.svg";
import lnBeatsIcon from "@/assets/platform-icons/ln-beats.svg";
import ivooxIcon from "@/assets/platform-icons/ivoox.svg";
import iheartradioIcon from "@/assets/platform-icons/iheartradio.svg";
import gpodderIcon from "@/assets/platform-icons/gpodder.svg";
import goodpodsIcon from "@/assets/platform-icons/goodpods.svg";
import globalPlayerIcon from "@/assets/platform-icons/global-player.svg";
import fountainIcon from "@/assets/platform-icons/fountain.svg";
import downcastIcon from "@/assets/platform-icons/downcast.svg";
import deezerIcon from "@/assets/platform-icons/deezer.svg";
import curiocasterIcon from "@/assets/platform-icons/curiocaster.svg";
import castroIcon from "@/assets/platform-icons/castro.svg";
import castboxIcon from "@/assets/platform-icons/castbox.svg";
import castamaticIcon from "@/assets/platform-icons/castamatic.svg";
import breezIcon from "@/assets/platform-icons/breez.svg";
import audibleIcon from "@/assets/platform-icons/audible.svg";
import applePodcastsIcon from "@/assets/platform-icons/apple-podcasts.svg";
import anytimeIcon from "@/assets/platform-icons/anytime-podcast-player.svg";
import antennapodIcon from "@/assets/platform-icons/antennapod.svg";

// Map platform IDs to imported icon assets
export const PLATFORM_ICON_MAP: Record<string, string | null> = {
  'spotify': spotifyIcon,
  'youtube-music': youtubeMusicIcon,
  'tunein': tuneinIcon,
  'truefans': truefansIcon,
  'rssradio': rssradioIcon,
  'podverse': podverseIcon,
  'podurama': poduramaIcon,
  'podstation': podstationIcon,
  'podcast-republic': podcastRepublicIcon,
  'podcast-guru': podcastGuruIcon,
  'podcast-app': podcastAppIcon,
  'podcast-addict': podcastAddictIcon,
  'podbean': podbeanIcon,
  'pocketcasts': pocketcastsIcon,
  'player-fm': playerFmIcon,
  'pandora': pandoraIcon,
  'overcast': overcastIcon,
  'moon-fm': moonFmIcon,
  'ln-beats': lnBeatsIcon,
  'ivoox': ivooxIcon,
  'iheartradio': iheartradioIcon,
  'gpodder': gpodderIcon,
  'goodpods': goodpodsIcon,
  'global-player': globalPlayerIcon,
  'fountain': fountainIcon,
  'downcast': downcastIcon,
  'deezer': deezerIcon,
  'curiocaster': curiocasterIcon,
  'castro': castroIcon,
  'castbox': castboxIcon,
  'castamatic': castamaticIcon,
  'breez': breezIcon,
  'audible': audibleIcon,
  'apple-podcasts': applePodcastsIcon,
  'anytime-podcast-player': anytimeIcon,
  'antennapod': antennapodIcon,
};

interface PlatformIconProps {
  platformId: string;
  platformName?: string;
  className?: string;
}

// Unified platform icon component — renders real SVG image or letter fallback
export function PlatformIcon({ platformId, platformName, className }: PlatformIconProps) {
  const iconSrc = PLATFORM_ICON_MAP[platformId] ?? null;
  const name = platformName || platformId;

  if (iconSrc) {
    return (
      <img
        src={iconSrc}
        alt={name}
        className={cn("h-4 w-4 rounded", className)}
        loading="lazy"
        decoding="async"
      />
    );
  }

  // Letter fallback for platforms without uploaded icons
  return (
    <span
      className={cn(
        "h-4 w-4 rounded-full bg-neutral/20 flex items-center justify-center text-[8px] font-bold text-foreground/60",
        className
      )}
      aria-label={name}
    >
      {name.charAt(0)}
    </span>
  );
}

// Legacy exports for backward compatibility
export const PLATFORM_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {};
